// Per-person notification settings: for each kind that applies to the
// signed-in person (their role + the active org's apps), choose In app
// and/or Email. Each change saves immediately; a failed save reverts
// that switch and shows why.
//
// Self-contained: rendered inside the NotificationsBell popover, and
// mountable on its own (e.g. a profile page).

import { useCallback, useEffect, useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn, InfoTip } from '@cyber-aware/ui';
import { useAuth } from '../../auth/AuthContext';
import { NOTIFICATION_INFO } from '../../lib/infoCopy/policies';
import { supportReference } from '../../lib/notificationFormat';
import {
  getNotificationSettings,
  updateNotificationSettings,
  type NotificationKind,
  type NotificationKindSetting,
} from '../../lib/notifications';

type Channel = 'inApp' | 'email';

export interface NotificationSettingsProps {
  className?: string;
}

export function NotificationSettings({ className }: NotificationSettingsProps) {
  const { session } = useAuth();
  const [kinds, setKinds] = useState<NotificationKindSetting[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const headingId = useId();

  const load = useCallback(async () => {
    if (!session) return;
    setLoadError(null);
    setKinds(null);
    try {
      const res = await getNotificationSettings(await session.user.getIdToken());
      setKinds(res.kinds);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load, session?.activeOrgId]);

  const toggle = async (kind: NotificationKind, channel: Channel, value: boolean) => {
    if (!session || !kinds) return;
    const previous = kinds;
    setKinds(kinds.map((k) => (k.kind === kind ? { ...k, [channel]: value } : k)));
    setSaving(`${kind}:${channel}`);
    setSaveError(null);
    try {
      const res = await updateNotificationSettings(await session.user.getIdToken(), {
        [kind]: { [channel]: value },
      });
      setKinds(res.kinds);
    } catch (err) {
      setKinds(previous);
      const ref = supportReference(err instanceof Error ? err.message : String(err));
      setSaveError(`Couldn’t save that change. Try again.${ref ? ` (Reference ${ref})` : ''}`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <section aria-labelledby={headingId} className={cn('space-y-3', className)}>
      <div>
        <div className="flex items-center gap-1.5">
          <h2 id={headingId} className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Notification settings
          </h2>
          <InfoTip content={NOTIFICATION_INFO.channels} />
        </div>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          Choose what reaches you and where. These settings follow you across every organization you belong to.
        </p>
      </div>

      {loadError ? (
        <div role="alert" className="rounded-md border border-slate-200 p-3 text-sm dark:border-slate-700">
          <p className="text-slate-700 dark:text-slate-200">We couldn’t load your notification settings right now.</p>
          {supportReference(loadError) && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              If this keeps happening, contact support with reference {supportReference(loadError)}.
            </p>
          )}
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 rounded-md px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-indigo-300 dark:hover:bg-indigo-950"
          >
            Try again
          </button>
        </div>
      ) : kinds === null ? (
        <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading settings…
        </p>
      ) : kinds.length === 0 ? (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          There’s nothing to set up for your role yet. Notifications here go to the people who manage your organization.
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
          {kinds.map((k) => (
            <li key={k.kind} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{k.label}</p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{k.description}</p>
              </div>
              <div className="flex shrink-0 gap-4">
                {(['inApp', 'email'] as const).map((channel) => (
                  <label
                    key={channel}
                    className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <input
                      type="checkbox"
                      checked={k[channel]}
                      disabled={saving !== null}
                      onChange={(e) => void toggle(k.kind, channel, e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 accent-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-600"
                      aria-label={`${k.label}: ${channel === 'inApp' ? 'in app' : 'email'}`}
                    />
                    {channel === 'inApp' ? 'In app' : 'Email'}
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      {saveError && (
        <p role="alert" className="text-xs text-rose-700 dark:text-rose-300">
          {saveError}
        </p>
      )}
    </section>
  );
}
